// Client-side world model: every entity (players + monsters) inside our area
// of interest, plus money piles on the ground.
//
// Movement is deterministic, so there is one rule for every unit: walk toward
// its current waypoint at its speed (dead-reckoning between events). The server
// owns HP/combat; we just store what it tells us. SELF is predicted in main.ts.

import type { EntityState, EntityKind, MoneyState } from "@mmo/shared";

export interface ViewEntity {
  id: number;
  kind: EntityKind;
  name: string;
  color: string;
  tx: number;
  ty: number;
  moving: boolean;
  speed: number;
  hp: number;
  maxHp: number;
  // Position drawn this frame (walked toward tx,ty each step).
  renderX: number;
  renderY: number;
}

export class WorldState {
  selfId = -1;
  worldW = 0;
  worldH = 0;

  // Every entity we know about, including self (kept for name/color/hp; self is
  // drawn from the predicted position in main.ts, not from here).
  readonly entities = new Map<number, ViewEntity>();
  // Money piles on the ground, keyed by id.
  readonly money = new Map<number, MoneyState>();

  setSelf(id: number): void {
    this.selfId = id;
  }

  setWorld(w: number, h: number): void {
    this.worldW = w;
    this.worldH = h;
  }

  /**
   * Drop all known entities + money. Called on every (re)connect so stale
   * entities from a previous session (which carry old netIds) don't linger as
   * ghosts alongside the fresh enter dump — the cause of duplicate characters.
   */
  reset(): void {
    this.entities.clear();
    this.money.clear();
  }

  /** Entities became visible (an `enter` event / the initial dump). */
  addEntities(entities: EntityState[]): void {
    for (const p of entities) {
      const existing = this.entities.get(p.id);
      if (existing) {
        existing.kind = p.kind;
        existing.name = p.name;
        existing.color = p.color;
        existing.tx = p.tx;
        existing.ty = p.ty;
        existing.moving = p.moving;
        existing.speed = p.speed;
        existing.hp = p.hp;
        existing.maxHp = p.maxHp;
        existing.renderX = p.x;
        existing.renderY = p.y;
      } else {
        this.entities.set(p.id, {
          id: p.id,
          kind: p.kind,
          name: p.name,
          color: p.color,
          tx: p.tx,
          ty: p.ty,
          moving: p.moving,
          speed: p.speed,
          hp: p.hp,
          maxHp: p.maxHp,
          renderX: p.x,
          renderY: p.y,
        });
      }
    }
  }

  /** A known entity got a new waypoint (we keep render position continuous). */
  applyPath(id: number, tx: number, ty: number, moving: boolean): void {
    const e = this.entities.get(id);
    if (!e) return;
    e.tx = tx;
    e.ty = ty;
    e.moving = moving;
  }

  /** An entity's HP changed. */
  setHp(id: number, hp: number): void {
    const e = this.entities.get(id);
    if (e) e.hp = hp;
  }

  /** An entity's display name changed. */
  setEntityName(id: number, name: string): void {
    const e = this.entities.get(id);
    if (e) e.name = name;
  }

  /** An entity was authoritatively repositioned (respawn). Snap it. */
  teleport(id: number, x: number, y: number): void {
    const e = this.entities.get(id);
    if (e) {
      e.renderX = x;
      e.renderY = y;
      e.tx = x;
      e.ty = y;
      e.moving = false;
    }
  }

  /** An entity is no longer visible (left the AOI, died, or disconnected). */
  leave(id: number): void {
    this.entities.delete(id);
  }

  addMoney(m: MoneyState): void {
    this.money.set(m.id, m);
  }

  removeMoney(id: number): void {
    this.money.delete(id);
  }

  /** The view entry for self (undefined until the initial enter arrives). */
  self(): ViewEntity | undefined {
    return this.entities.get(this.selfId);
  }

  /** Count of visible players only (entities also include monsters). */
  playerCount(): number {
    let n = 0;
    for (const e of this.entities.values()) if (e.kind === "player") n++;
    return n;
  }

  /** Walk every OTHER entity toward its destination this frame. */
  step(dt: number): void {
    for (const e of this.entities.values()) {
      if (e.id === this.selfId) continue;
      const dx = e.tx - e.renderX;
      const dy = e.ty - e.renderY;
      const dist = Math.hypot(dx, dy);
      if (dist === 0) continue;
      const stepLen = e.speed * dt;
      if (dist <= stepLen) {
        e.renderX = e.tx;
        e.renderY = e.ty;
        e.moving = false;
      } else {
        e.renderX += (dx / dist) * stepLen;
        e.renderY += (dy / dist) * stepLen;
      }
    }
  }
}
